// Copyright (c) 2026 Dyna Robotics, Inc. All rights reserved.
// Dyna Robotics Proprietary and Confidential.
// Unauthorized copying, use, or distribution is prohibited.
// SPDX-License-Identifier: LicenseRef-Dyna-Internal

/**
 * The browser's half of serial normalization (design doc §1.2, §10.3).
 *
 * This is a **port of the server's rule, not a second opinion**: the same trimming, the
 * same upper-casing, the same placeholder list. §10.3 requires typed and scanned input to
 * go through one function so a scan and a keystroke cannot disagree about what was entered.
 *
 * The server normalizes again and re-validates regardless (§10.3). Nothing here is trusted;
 * it exists so the operator sees the duplicate or the empty slot immediately rather than
 * after a round trip.
 *
 * Kept in step with `app/domains/archive/serials.py` — a test asserts the placeholder lists
 * match.
 */

/** Includes the full-width space an IME produces and the newline a scanner appends. */
const TRIMMED = /^[\s　]+|[\s　]+$/g;

export const PLACEHOLDER_VALUES = new Set([
  "",
  "-",
  "--",
  "—",
  "/",
  "N/A",
  "NA",
  "N.A.",
  "0",
  "NULL",
  "NONE",
  "NIL",
  "无",
  "空",
  "未知",
]);

export function normalizeSerial(raw: string | null | undefined): string {
  if (raw == null) return "";
  return raw.replace(TRIMMED, "").toUpperCase();
}

/** A value that is a refusal to answer rather than an answer (§8.3). */
export function isPlaceholder(normalized: string): boolean {
  return PLACEHOLDER_VALUES.has(normalized);
}

export function isUsableSerial(raw: string | null | undefined): boolean {
  return !isPlaceholder(normalizeSerial(raw));
}

/** Device numbers keep their case — the column is case-insensitive already (§8.3). */
export function normalizeDeviceNo(raw: string | null | undefined): string {
  if (raw == null) return "";
  return raw.replace(TRIMMED, "");
}
